/**
 * RadarDataReceiver.js
 * 处理雷达数据接收和解析的专用模块
 */
import Vue from 'vue';
import appConfig from '@/config/app-config';
import signalRManager from './signalr-manager'; // 改为导入signalRManager

// 事件总线，用于发布雷达数据更新事件
const eventBus = new Vue();

class RadarDataReceiver {
  constructor() {
    this._targets = [];
    this._isConnected = false;
    this._isSubscribed = false;
    this._signalR = null;
    this._options = appConfig.signalR.options;
    this._serverUrl = appConfig.signalR.serverUrl;
    this._topic = appConfig.radar.topic;
    this._messageType = appConfig.radar.messageType;
    
    // 添加地图就绪状态和重试机制
    this._mapReady = false;
    this._connectRetryCount = 0;
    this._maxConnectRetries = 3; // 雷达接收器重试次数少于AIS接收器
    this._connectRetryInterval = 2500; // 重试间隔稍长于AIS接收器
    this._connectingPromise = null;
    this._subscribeRetryCount = 0;
    this._maxSubscribeRetries = 3;
    this._subscribeRetryInterval = 2000;
    this._subscribingPromise = null;
    
    // 添加目标过期清理
    this._targetExpirationTime = 60000; // 60秒无更新则视为过期
    this._cleanupInterval = 30000; // 每30秒清理一次过期目标
    this._cleanupTimer = null;
    
    // 启动清理定时器
    this._startCleanupTimer();
  }

  /**
   * 初始化雷达数据接收器
   * @param {Object} vueInstance - Vue实例
   * @param {Object} options - 配置选项
   */
  init(vueInstance, options = {}) {
    // 合并选项
    this._options = {
      ...this._options,
      ...options
    };
    
    // 使用signalRManager而非直接创建SignalRClient实例
    this._signalR = signalRManager;
    
    // 确保signalRManager已初始化
    if (!this._signalR._isInitialized) {
      this._signalR.init(this._options);
    }
    
    console.log('RadarDataReceiver已初始化，服务器地址:', this._serverUrl);
  }

  /**
   * 连接到服务器并订阅雷达数据
   * @returns {Promise<void>}
   */
  async connect() {
    if (!this._signalR) {
      throw new Error('请先调用init方法初始化RadarDataReceiver');
    }

    // 注册雷达消息处理器 - 使用signalRManager的onMessage方法
    console.log('RadarDataReceiver注册雷达消息处理器:', this._messageType);
    this._signalR.onMessage(this._messageType, this._handleRadarMessage.bind(this));
    
    // 注册连接状态变更处理 - 使用signalRManager的onConnectionStatusChanged方法
    this._signalR.onConnectionStatusChanged(this._handleConnectionStatusChanged.bind(this));
    
    // 如果已有连接中的Promise，则返回该Promise
    if (this._connectingPromise) {
      console.log('雷达连接已在进行中，等待连接完成...');
      return this._connectingPromise;
    }
    
    // 创建连接Promise
    this._connectingPromise = (async () => {
      try {
        console.log(`RadarDataReceiver正在连接到服务器: ${this._serverUrl}`);
        
        // 检查SignalR是否已连接，复用现有连接
        if (!this._signalR.isConnected) {
          // 使用connect方法而非ensureConnection
          await this._signalR.connect(this._serverUrl, {
            clientType: 'RADAR_RECEIVER',
            clientId: 'radar_' + Date.now()
          });
          console.log('雷达数据接收器连接成功');
        } else {
          console.log('SignalR已连接，复用现有连接');
        }
        
        // 连接成功后，尝试订阅
        await this._subscribeWithRetry();
        
        this._isConnected = true;
        console.log('RadarDataReceiver已连接并订阅雷达数据主题');
        
        // 重置重试计数器
        this._connectRetryCount = 0;
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('RadarDataReceiver连接或订阅失败:', error);
        
        // 检查连接重试次数
        if (this._connectRetryCount < this._maxConnectRetries) {
          this._connectRetryCount++;
          console.log(`雷达连接失败，${this._connectRetryInterval/1000}秒后进行第${this._connectRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._connectRetryInterval));
          this._connectingPromise = null; // 清除当前Promise
          return this.connect(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._connectingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._connectingPromise;
  }

  /**
   * 带重试的订阅方法
   * @private
   */
  async _subscribeWithRetry() {
    // 如果已有订阅中的Promise，则返回该Promise
    if (this._subscribingPromise) {
      return this._subscribingPromise;
    }
    
    this._subscribingPromise = (async () => {
      try {
        // 检查连接状态
        if (!this._signalR.isConnected) {
          throw new Error('无法订阅：SignalR未连接');
        }
        
        console.log(`尝试订阅雷达数据主题: ${this._topic}`);
        await this._signalR.subscribe(this._topic);
        this._isSubscribed = true;
        console.log('雷达数据订阅成功');
        
        // 重置重试计数器
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('雷达数据订阅失败:', error);
        
        // 检查订阅重试次数
        if (this._subscribeRetryCount < this._maxSubscribeRetries) {
          this._subscribeRetryCount++;
          console.log(`订阅失败，${this._subscribeRetryInterval/1000}秒后进行第${this._subscribeRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._subscribeRetryInterval));
          this._subscribingPromise = null; // 清除当前Promise
          return this._subscribeWithRetry(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._subscribingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._subscribingPromise;
  }

  /**
   * 处理连接状态变化
   * @param {boolean} isConnected - 是否已连接
   * @param {string} connectionId - 连接ID
   * @param {string} detail - 详细信息
   * @private
   */
  _handleConnectionStatusChanged(isConnected, connectionId, detail) {
    console.log('RadarDataReceiver连接状态变更:', isConnected, connectionId, detail || '已连接');
    
    // 更新连接状态
    const previousState = this._isConnected;
    this._isConnected = isConnected;
    
    // 如果连接断开后又重新连接，需要重新订阅
    if (isConnected && !this._isSubscribed) {
      // 使用延迟重新订阅，避免与其他组件竞争
      setTimeout(() => {
        if (this._isConnected && !this._isSubscribed) {
          console.log('雷达连接已恢复，尝试重新订阅...');
          // 使用带重试的订阅方法，而不是直接调用subscribe
          this._subscribeWithRetry().then(() => {
            console.log('RadarDataReceiver重新订阅成功');
          }).catch(err => {
            console.error('RadarDataReceiver重新订阅失败:', err);
          });
        }
      }, 1500); // 比AIS接收器晚500ms重新订阅
    } else if (!isConnected && previousState) {
      // 连接断开，更新订阅状态
      this._isSubscribed = false;
      console.log('雷达连接断开，已标记为未订阅');
    }
    
    // 触发连接状态变更事件
    eventBus.$emit('radar-connection-status-changed', {
      isConnected,
      connectionId,
      detail
    });
  }

  /**
   * 处理接收到的雷达消息
   * @param {Object} message - 接收到的消息
   * @param {Object} parsedData - 解析后的数据
   * @private
   */
  _handleRadarMessage(message, parsedData) {
    // 检查消息是否是雷达数据
    if (message.type !== this._messageType) {
      return;
    }
    
    try {
      const radarData = parsedData;
      
      if (!radarData || !radarData.targetId) {
        console.warn('RadarDataReceiver: 无效的雷达数据:', radarData);
        return;
      }
      
      // 处理雷达数据
      this._processRadarData(radarData);
    } catch (error) {
      console.error('RadarDataReceiver处理雷达数据失败:', error);
    }
  }
  
  /**
   * 处理雷达数据
   * @param {Object} radarData - 雷达数据
   * @private
   */
  _processRadarData(radarData) {
    // 检查是否有positions数组
    if (radarData.positions && Array.isArray(radarData.positions) && radarData.positions.length > 0) {
      // console.log(`RadarDataReceiver: 处理雷达目标 ${radarData.targetId} 的 ${radarData.positions.length} 个位置数据`);
      
      // 优先使用latestPosition
      if (radarData.latestPosition) {
        this._processRadarPosition(radarData.latestPosition);
      } else {
        // 处理positions数组中的最后一个位置（最新位置）
        this._processRadarPosition(radarData.positions[radarData.positions.length - 1]);
      }
    } else if (radarData.latestPosition) {
      // 直接处理latestPosition
      this._processRadarPosition(radarData.latestPosition);
    } else {
      // 将整个数据作为位置数据处理
      this._processRadarPosition(radarData);
    }
  }
  
  /**
   * 处理单个雷达位置数据
   * @param {Object} positionData - 位置数据
   * @private
   */
  _processRadarPosition(positionData) {
    // 验证经纬度信息
    if (positionData.longitude === null || positionData.latitude === null) {
      console.debug('RadarDataReceiver: 雷达目标数据缺少位置信息:', positionData.targetId);
      return;
    }
    
    // 转换为标准雷达目标对象格式
    const target = this._convertToRadarTargetObject(positionData);
    
    // 更新或添加雷达目标数据
    const existingTargetIndex = this._targets.findIndex(t => t.targetId === target.targetId);
    if (existingTargetIndex >= 0) {
      this._targets[existingTargetIndex] = target;
    } else {
      this._targets.push(target);
    }
    
    // 发布雷达目标更新事件
    eventBus.$emit('radar-data-updated', {
      target,
      isNew: existingTargetIndex < 0,
      targets: this._targets
    });
  }

  /**
   * 将原始数据转换为标准雷达目标对象
   * @param {Object} rawData - 原始雷达数据
   * @returns {Object} 标准雷达目标对象
   * @private
   */
  _convertToRadarTargetObject(rawData) {
    // console.log('RadarDataReceiver: 原始雷达数据:', rawData);
    
    // 处理经纬度坐标转换
    const { longitude, latitude } = this._transformCoordinates(rawData);
    
    // 处理尺寸信息
    const length = rawData.length || 0;
    const width = rawData.width || length * 0.3 || 0; // 如果没有宽度，估算为长度的30%
    
    // 处理时间戳
    const timestamp = rawData.timestamp || rawData.createTime || new Date().toISOString();
    
    // 处理航向和船首向
    let heading = parseFloat(rawData.heading);
    const course = parseFloat(rawData.course) || 0;
    
    // 检查heading是否有效
    if (isNaN(heading) || heading === 511) {
      console.debug(`RadarDataReceiver: 雷达目标 ${rawData.targetId} 的heading无效(${heading})，使用course(${course})`);
      heading = null; // 将无效的heading设为null，让角度计算逻辑使用course
    }
    
    return {
      targetId: rawData.targetId,
      msgid: 'RADAR',
      targetName: rawData.targetName || `雷达目标-${rawData.targetId}`,
      pos: {
        longitude: longitude,
        latitude: latitude
      },
      course: course,
      heading: heading,
      speed: parseFloat(rawData.speed) || 0,
      targetType: rawData.targetType || 'UNKNOWN',
      distance: parseFloat(rawData.distance) || 0,
      bearing: parseFloat(rawData.bearing) || 0,
      dataSource: rawData.dataSource || 'radar_forwarder',
      size: {
        length: length,
        width: width
      },
      deviceId: rawData.deviceId || null,
      receive_time: timestamp
    };
  }

  /**
   * 转换坐标系（处理可能的坐标系差异）
   * @param {Object} data - 包含经纬度的数据对象
   * @returns {Object} 转换后的经纬度对象
   * @private
   */
  _transformCoordinates(data) {
    // 获取原始经纬度
    let longitude = parseFloat(data.longitude);
    let latitude = parseFloat(data.latitude);
    
    // 检查是否为有效数值
    if (isNaN(longitude) || isNaN(latitude)) {
      console.warn('RadarDataReceiver: 无效的经纬度值:', data.longitude, data.latitude);
      longitude = 0;
      latitude = 0;
    }
    
    // 检查经纬度是否在合理范围内
    if (Math.abs(longitude) > 180 || Math.abs(latitude) > 90) {
      console.warn('RadarDataReceiver: 经纬度超出范围，可能需要坐标转换:', longitude, latitude);
      
      // 如果经度是负值且明显不合理，可能需要转换
      if (longitude < -180) {
        longitude = 360 + longitude;
      }
      
      // 如果经度大于180，可能需要转换
      if (longitude > 180) {
        longitude = longitude - 360;
      }
      
      // 纬度限制在-90到90之间
      latitude = Math.max(-90, Math.min(90, latitude));
    }
    
    return { longitude, latitude };
  }

  /**
   * 设置地图就绪状态
   * @param {boolean} ready - 地图是否就绪
   */
  setMapReady(ready) {
    const wasReady = this._mapReady;
    this._mapReady = ready;
    
    if (!wasReady && ready) {
      console.log('RadarDataReceiver: 地图就绪状态设置为', ready);
    }
  }

  /**
   * 启动清理定时器
   * @private
   */
  _startCleanupTimer() {
    // 清除现有定时器
    if (this._cleanupTimer) {
      clearInterval(this._cleanupTimer);
    }
    
    // 设置新的清理定时器
    this._cleanupTimer = setInterval(() => {
      this._cleanupExpiredTargets();
    }, this._cleanupInterval);
    
    console.log(`RadarDataReceiver: 已启动目标清理定时器，间隔: ${this._cleanupInterval / 1000}秒`);
  }
  
  /**
   * 清理过期的雷达目标
   * @private
   */
  _cleanupExpiredTargets() {
    if (this._targets.length === 0) {
      return;
    }
    
    const now = new Date().getTime();
    const expiredTime = now - this._targetExpirationTime;
    const initialCount = this._targets.length;
    
    // 过滤出未过期的目标
    this._targets = this._targets.filter(target => {
      // 解析时间戳
      const targetTime = new Date(target.receive_time).getTime();
      const isValid = targetTime > expiredTime;
      
      if (!isValid) {
        console.debug(`RadarDataReceiver: 清理过期目标 ${target.targetId}, 最后更新: ${target.receive_time}`);
      }
      
      return isValid;
    });
    
    const removedCount = initialCount - this._targets.length;
    if (removedCount > 0) {
      console.log(`RadarDataReceiver: 已清理 ${removedCount} 个过期雷达目标`);
      
      // 发布目标列表更新事件
      eventBus.$emit('radar-targets-cleaned', {
        removedCount,
        remainingCount: this._targets.length,
        targets: this._targets
      });
    }
  }

  /**
   * 断开连接并清理资源
   * @returns {Promise<void>}
   */
  async disconnect() {
    if (this._isSubscribed && this._signalR) {
      try {
        await this._signalR.unsubscribe(this._topic);
        this._isSubscribed = false;
        console.log('RadarDataReceiver已取消订阅');
      } catch (err) {
        console.warn('RadarDataReceiver取消订阅失败:', err);
      }
    }
    
    // 清除清理定时器
    if (this._cleanupTimer) {
      clearInterval(this._cleanupTimer);
      this._cleanupTimer = null;
    }
    
    return;
  }

  /**
   * 获取当前雷达目标数据列表
   * @returns {Array} 雷达目标数据数组
   */
  get targets() {
    return [...this._targets]; // 返回副本，防止外部修改
  }

  /**
   * 获取连接状态
   * @returns {boolean} 是否已连接
   */
  get isConnected() {
    return this._isConnected;
  }

  /**
   * 获取订阅状态
   * @returns {boolean} 是否已订阅
   */
  get isSubscribed() {
    return this._isSubscribed;
  }

  /**
   * 获取雷达目标数量
   * @returns {number} 雷达目标数量
   */
  get targetCount() {
    return this._targets.length;
  }
}

// 事件监听器助手方法
const radarEvents = {
  /**
   * 监听雷达数据更新事件
   * @param {Function} callback - 回调函数
   */
  onRadarDataUpdated(callback) {
    eventBus.$on('radar-data-updated', callback);
  },

  /**
   * 移除雷达数据更新事件监听
   * @param {Function} callback - 回调函数
   */
  offRadarDataUpdated(callback) {
    eventBus.$off('radar-data-updated', callback);
  },

  /**
   * 监听连接状态变更事件
   * @param {Function} callback - 回调函数
   */
  onConnectionStatusChanged(callback) {
    eventBus.$on('radar-connection-status-changed', callback);
  },

  /**
   * 移除连接状态变更事件监听
   * @param {Function} callback - 回调函数
   */
  offConnectionStatusChanged(callback) {
    eventBus.$off('radar-connection-status-changed', callback);
  },
  
  /**
   * 监听雷达目标清理事件
   * @param {Function} callback - 回调函数
   */
  onTargetsCleaned(callback) {
    eventBus.$on('radar-targets-cleaned', callback);
  },
  
  /**
   * 移除雷达目标清理事件监听
   * @param {Function} callback - 回调函数
   */
  offTargetsCleaned(callback) {
    eventBus.$off('radar-targets-cleaned', callback);
  }
};

// 创建并导出单例实例
const radarDataReceiver = new RadarDataReceiver();

export { radarDataReceiver, radarEvents };
export default radarDataReceiver; 